import { ManagementTile } from 'app/components/management/title';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { useAllFavourites, useAllListings } from 'app/network/listing';
import { LoanFilterKey, LoanType, useAllLoans } from 'app/network/loan';
import { useUser } from 'app/network/user';
import { ListingType } from 'app/types/listing';
import { FeatureFlag, isFeatureEnabled, isLoanRoute } from 'app/utils/helpers';
import { useAlignmentIfDesktop, usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useRoute } from '@react-navigation/native';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyListingsCountFilters } from 'app/network/listing-query-filters';
import type * as management from '../navigator';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArchive, IconChevronRight, IconHeart } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { BonifiableListingsCountSuffix } from './bonifiable-listings-count-suffix';
const theme = createDSTheme();

type ActionDef = {
  [R in keyof management.Parameters]: {
    icon: ReactNode;
    route: R;
    routeParams?: management.Parameters[R];
    title: string;
    suffix?: ReactNode;
  };
}[keyof management.Parameters];

const countQueryOptions = {
  throwOnError: true,
  populateListingDetailCache: false,
} as const;

export const MenuManagment = () => {
  const { t } = useTranslation();
  const navigation = useNavigation<'management-overview'>();
  const route = useRoute();
  const selectedLoanType = isLoanRoute(route) ? route.params.type : undefined;
  const { isSmallUI } = useUISize();
  const setPageTitle = usePageTitle('management');
  useEffect(() => setPageTitle('title'), [setPageTitle]);
  const actionColor = theme.colors['surface.tertiary-neutral'];

  const userQuery = useUser();
  const { data: user } = userQuery;
  const hasResolvedUserId = Boolean(user?.id);

  const canPublish = isFeatureEnabled(FeatureFlag.publishListings, user?.featureFlags);

  const { data: offers } = useAllListings(
    getMyListingsCountFilters(Boolean(user?.id), user?.id, ListingType.offer),
    'ManagementOverview-Offers',
    { ...countQueryOptions, enabled: hasResolvedUserId },
  );
  const numberOfListingsToBonify = offers?.pages[0]?.numberOfBonifiableListings ?? 0;
  const { data: requests } = useAllListings(
    getMyListingsCountFilters(Boolean(user?.id), user?.id, ListingType.request),
    'ManagementOverview-Requests',
    { ...countQueryOptions, enabled: hasResolvedUserId },
  );
  const { data: favourites } = useAllFavourites({ throwOnError: true });

  const lendingLoansQuery = useAllLoans(LoanFilterKey.Management, LoanType.Lending);
  const { data: lendings } = lendingLoansQuery;

  const borrowingLoansQuery = useAllLoans(LoanFilterKey.Management, LoanType.Borrowing);
  const { data: borrowings } = borrowingLoansQuery;

  const publicationActions: ActionDef[] = canPublish
    ? [
        {
          icon: (
            <Column
              alignItems="center"
              justifyContent="center"
              style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: '#E4DFFF' }}
            >
              <Text fontSize={26}>🫴</Text>
            </Column>
          ),
          route: 'my-listings',
          routeParams: { type: ListingType.offer },
          title: t('title.offer', { ns: 'my-listings' }),
          suffix: (
            <BonifiableListingsCountSuffix bonifiableCount={numberOfListingsToBonify} count={offers?.pages[0]?.total} />
          ),
        },
        {
          icon: (
            <Column
              alignItems="center"
              justifyContent="center"
              style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: '#E4DFFF' }}
            >
              <Text fontSize={26}>👋</Text>
            </Column>
          ),
          route: 'my-listings',
          routeParams: { type: ListingType.request },
          title: t('title.request', { ns: 'my-listings' }),
          suffix: (
            <>
              <Column justifyContent="center" width={40}>
                <Text color="text.placeholder" fontSize={16} fontWeight="400">
                  {requests?.pages[0]?.total ?? '-'}
                </Text>
              </Column>
              <Column>
                <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
              </Column>
            </>
          ),
        },
      ]
    : [];

  const moreOptionsActions: ActionDef[] = [
    {
      icon: <IconHeart strokeWidth={ICON_STROKE_WIDTH} size={32} />,
      route: 'favourites',
      title: t('title', { ns: 'favourites' }),
      suffix: (
        <>
          <Column justifyContent="center" width={40}>
            <Text color="text.placeholder" fontSize={16} fontWeight="400">
              {favourites?.length ?? '-'}
            </Text>
          </Column>
          <Column>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
          </Column>
        </>
      ),
    },
    {
      icon: <IconArchive strokeWidth={ICON_STROKE_WIDTH} size={32} />,
      route: 'past-loans',
      title: t('past.title', { ns: 'loans' }),
    },
  ];

  const alignment = useAlignmentIfDesktop();

  return (
    <Column gap="spacing.general.x4">
      <Text fontWeight="600" textAlign={alignment} variant="headerXSmall">
        {t('title', { ns: 'management' })}
      </Text>

      {isSmallUI ? (
        <Row flex={1} gap="spacing.general.x4">
          <ManagementTile
            loans={lendings}
            navigation={navigation}
            selectedLoanType={selectedLoanType}
            type={LoanType.Lending}
          />
          <ManagementTile
            loans={borrowings}
            navigation={navigation}
            selectedLoanType={selectedLoanType}
            type={LoanType.Borrowing}
          />
        </Row>
      ) : (
        <Column gap="spacing.general.x4">
          <ManagementTile
            loans={lendings}
            navigation={navigation}
            selectedLoanType={selectedLoanType}
            type={LoanType.Lending}
          />
          <ManagementTile
            loans={borrowings}
            navigation={navigation}
            selectedLoanType={selectedLoanType}
            type={LoanType.Borrowing}
          />
        </Column>
      )}

      {publicationActions.length > 0 && (
        <Column gap="spacing.general.x4">
          <Text fontWeight="600" textAlign={alignment} variant="bodyLarge">
            {t('publications', { ns: 'management' })}
          </Text>
          {publicationActions.map((action) => (
            <Button
              key={action.title}
              testID={
                action.route === 'my-listings' && action.routeParams?.type === ListingType.offer
                  ? 'management-my-offers-link'
                  : undefined
              }
              onPress={() =>
                // @ts-expect-error tuple union thing?
                navigation.navigate(action.route, action.routeParams, { pop: true })
              }
              prefix={action.icon}
              style={{
                marginLeft: -40,
                paddingLeft: 40,
                backgroundColor: actionColor,
              }}
              suffix={action.suffix ?? <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
            >
              <Text fontWeight="700" style={{ flex: 1 }}>
                {action.title}
              </Text>
            </Button>
          ))}
        </Column>
      )}

      <Column gap="spacing.general.x4">
        <Text fontWeight="600" textAlign={alignment} variant="bodyLarge">
          {t('moreOptions', { ns: 'management' })}
        </Text>
        {moreOptionsActions.map((action) => (
          <Button
            key={action.title}
            onPress={() =>
              // @ts-expect-error tuple union thing?
              navigation.navigate(action.route, action.routeParams, { pop: true })
            }
            prefix={action.icon}
            style={{
              marginLeft: -40,
              paddingLeft: 40,
            }}
            suffix={action.suffix ?? <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
          >
            <Text fontWeight="600" style={{ flex: 1 }}>
              {action.title}
            </Text>
          </Button>
        ))}
      </Column>
    </Column>
  );
};

export const SideMenuManagment = MenuManagment;
